import { useRef, useState } from 'react';
import toast from 'react-hot-toast';
import { Upload, Loader2, FileText } from 'lucide-react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from '../../../components/Dialog';
import { Button } from '../../../components/Button';
import { Badge } from '../../../components/Badge';
import { useProfileStore, type ImportSummary } from '../../../store/profileStore';

/** Header text (lowercased) → payload key the import API understands. */
const HEADER_MAP: Record<string, string> = {
  'employee code': 'employeeCode', 'code': 'employeeCode', 'emp id': 'employeeCode', 'employee id': 'employeeCode',
  'first name': 'firstName', 'last name': 'lastName', 'email': 'email', 'work email': 'email',
  'phone': 'phone', 'work phone': 'phone', 'department': 'department', 'designation': 'designation',
  'employment type': 'employmentType',
  'personal email': 'personalEmail', 'personal phone': 'personalPhone',
  'date of birth': 'dateOfBirth', 'dob': 'dateOfBirth', 'gender': 'gender', 'marital status': 'maritalStatus',
  'blood group': 'bloodGroup', 'nationality': 'nationality', 'current address': 'currentAddress',
  'permanent address': 'permanentAddress', 'emergency contact name': 'emergencyContactName',
  'emergency contact phone': 'emergencyContactPhone', 'emergency contact relation': 'emergencyContactRelation',
};

/** Minimal CSV parser that respects double-quoted fields and escaped quotes. */
function parseCsv(text: string): string[][] {
  const rows: string[][] = [];
  let row: string[] = [], cur = '', inQ = false;
  for (let i = 0; i < text.length; i++) {
    const c = text[i];
    if (inQ) {
      if (c === '"') { if (text[i + 1] === '"') { cur += '"'; i++; } else inQ = false; }
      else cur += c;
    } else if (c === '"') inQ = true;
    else if (c === ',') { row.push(cur); cur = ''; }
    else if (c === '\n') { row.push(cur); rows.push(row); row = []; cur = ''; }
    else if (c === '\r') { /* ignore */ }
    else cur += c;
  }
  if (cur !== '' || row.length) { row.push(cur); rows.push(row); }
  return rows.filter((r) => r.some((c) => c.trim() !== ''));
}

function toRows(text: string): any[] {
  const grid = parseCsv(text);
  if (grid.length < 2) return [];
  const headers = grid[0].map((h) => HEADER_MAP[h.trim().toLowerCase()] || null);
  return grid.slice(1).map((cells) => {
    const o: Record<string, string> = {};
    headers.forEach((key, i) => { if (key && cells[i] !== undefined) o[key] = cells[i].trim(); });
    return o;
  });
}

export function EmployeeImportDialog({ open, onOpenChange, onImported }: {
  open: boolean; onOpenChange: (v: boolean) => void; onImported: () => void;
}) {
  const importEmployees = useProfileStore((s) => s.importEmployees);
  const fileRef = useRef<HTMLInputElement>(null);
  const [rows, setRows] = useState<any[]>([]);
  const [fileName, setFileName] = useState('');
  const [preview, setPreview] = useState<ImportSummary | null>(null);
  const [busy, setBusy] = useState(false);

  const reset = () => { setRows([]); setFileName(''); setPreview(null); if (fileRef.current) fileRef.current.value = ''; };

  const onFile = async (file: File) => {
    const parsed = toRows(await file.text());
    if (parsed.length === 0) { toast.error('No valid rows found. Check the headers.'); return; }
    setFileName(file.name);
    setRows(parsed);
    setBusy(true);
    try { setPreview(await importEmployees(parsed, true)); }
    catch (e: any) { toast.error(e?.response?.data?.message || 'Could not read the file'); }
    finally { setBusy(false); }
  };

  const apply = async () => {
    setBusy(true);
    try {
      const res = await importEmployees(rows, false);
      toast.success(`Imported — ${res.create} created, ${res.update} updated${res.errors ? `, ${res.errors} failed` : ''}`);
      onImported();
      onOpenChange(false);
      reset();
    } catch (e: any) {
      toast.error(e?.response?.data?.message || 'Import failed');
    } finally { setBusy(false); }
  };

  return (
    <Dialog open={open} onOpenChange={(v) => { onOpenChange(v); if (!v) reset(); }}>
      <DialogContent className="max-w-2xl">
        <DialogHeader>
          <DialogTitle>Import employees</DialogTitle>
          <DialogDescription>
            Upload a CSV. Rows matching an existing employee code are updated; new codes create a new employee and account.
            Reporting manager and salary aren’t imported — set those in the app.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4">
          <div className="flex items-center gap-3">
            <input ref={fileRef} type="file" accept=".csv,text/csv" className="hidden"
              onChange={(e) => { const f = e.target.files?.[0]; if (f) onFile(f); }} />
            <Button variant="secondary" size="sm" onClick={() => fileRef.current?.click()} disabled={busy}>
              <Upload className="size-4" /> Choose CSV
            </Button>
            {fileName && <span className="flex items-center gap-1.5 text-sm text-muted-foreground"><FileText className="size-4" /> {fileName}</span>}
            {busy && <Loader2 className="size-4 animate-spin text-muted-foreground" />}
          </div>

          {preview && (
            <div className="space-y-3">
              <div className="flex flex-wrap gap-2">
                <Badge variant="success">{preview.create} to create</Badge>
                <Badge variant="info">{preview.update} to update</Badge>
                {preview.errors > 0 && <Badge variant="destructive">{preview.errors} with errors</Badge>}
                <Badge variant="muted">{preview.total} rows</Badge>
              </div>
              <div className="max-h-64 overflow-auto rounded-lg border border-border">
                <table className="w-full text-left text-sm">
                  <thead className="sticky top-0 bg-card text-xs text-muted-foreground">
                    <tr className="border-b border-border">
                      <th className="px-3 py-2">Row</th><th className="px-3 py-2">Name</th>
                      <th className="px-3 py-2">Code</th><th className="px-3 py-2">Action</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-border-12">
                    {preview.results.map((r) => (
                      <tr key={r.row}>
                        <td className="px-3 py-1.5 text-muted-foreground">{r.row}</td>
                        <td className="px-3 py-1.5 text-foreground">{r.name}</td>
                        <td className="px-3 py-1.5 text-muted-foreground">{r.code || '—'}</td>
                        <td className="px-3 py-1.5">
                          {r.action === 'error'
                            ? <span className="text-destructive-strong">Error: {r.message}</span>
                            : <Badge variant={r.action === 'create' ? 'success' : 'info'}>{r.action}</Badge>}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}
        </div>

        <DialogFooter>
          <Button variant="secondary" size="sm" onClick={() => onOpenChange(false)}>Cancel</Button>
          <Button size="sm" disabled={!preview || busy || (preview.create + preview.update === 0)} onClick={apply}>
            {busy ? <Loader2 className="size-4 animate-spin" /> : `Import ${preview ? preview.create + preview.update : ''}`}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
